import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { apiClient } from '../../lib/api-client.js';
import { Spinner } from '../../components/Spinner.js';

interface BlockedUser {
  id: string;
  nickname: string;
  avatarUrl: string | null;
  blockedAt: string;
}

function Avatar({ url, nickname }: { url: string | null; nickname: string }) {
  if (url) {
    return (
      <img src={url} alt={nickname} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
    );
  }
  return (
    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
      <span className="text-gray-500 text-sm font-semibold">{nickname[0]}</span>
    </div>
  );
}

export function BlockListView() {
  const navigate = useNavigate();
  const [blockedUsers, setBlockedUsers] = useState<BlockedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [unblocking, setUnblocking] = useState<string | null>(null);

  const fetchBlockList = useCallback(async () => {
    try {
      const data = await apiClient.get<BlockedUser[]>('/blocks', { requireAuth: true });
      setBlockedUsers(data ?? []);
    } catch {
      // エラー無視
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchBlockList();
  }, [fetchBlockList]);

  const handleUnblock = async (userId: string) => {
    setUnblocking(userId);
    try {
      await apiClient.delete(`/blocks/${userId}`, { requireAuth: true });
      setBlockedUsers((prev) => prev.filter((u) => u.id !== userId));
    } catch {
      alert('ブロック解除に失敗しました。もう一度お試しください。');
    } finally {
      setUnblocking(null);
    }
  };

  return (
    <div className="max-w-2xl mx-auto pb-8">
      <div className="flex items-center gap-3 px-4 py-4 border-b border-gray-100">
        <button
          className="p-1 text-gray-500 hover:text-gray-700"
          onClick={() => navigate(-1)}
          aria-label="戻る"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M15 18l-6-6 6-6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <h1 className="text-lg font-bold text-gray-900">ブロックリスト</h1>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Spinner />
        </div>
      ) : blockedUsers.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" className="mb-3">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
            <line
              x1="4.93"
              y1="4.93"
              x2="19.07"
              y2="19.07"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          <p className="text-sm">ブロック中のユーザーはいません</p>
        </div>
      ) : (
        <ul className="divide-y divide-gray-100">
          {blockedUsers.map((user) => (
            <li key={user.id} className="flex items-center gap-3 px-4 py-3">
              <Avatar url={user.avatarUrl} nickname={user.nickname} />
              <span className="flex-1 text-sm font-medium text-gray-900 truncate">
                {user.nickname}
              </span>
              <button
                onClick={() => void handleUnblock(user.id)}
                disabled={unblocking === user.id}
                className="flex-shrink-0 px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-300 rounded-full hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {unblocking === user.id ? '解除中...' : 'ブロック解除'}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
